<script lang="ts">
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		artistSectionQueryKey,
		getArtistExhibitions,
	} from '#lib/websites/artists/api/getArtistSections.js';
	import {
		ARTIST_PROJECTS_LIMIT,
		EXHIBITIONS_SORT,
	} from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import { transformInfiniteDataExhibitions } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformInfiniteDataExhibitions/index.js';
	import { GetArtistPageExhibitionsDocument } from '#lib/websites/artists/queries/__generated__/getArtistPageExhibitions.generated.js';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import { ExhibitionCard } from '$global/features/exhibitions/components/ExhibitionCard';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { hasMoreInfiniteItems } from '$global/utils/infinite-loading/hasMoreInfiniteItems';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import { shouldRestoreInfiniteItems } from '$global/utils/infinite-loading/shouldRestoreInfiniteItems';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-exhibitions`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let variables = $derived(page.data.variables as ArtistsPageData['variables']);
	let count = $derived(artist.totalExhibitions?.count || 0);

	// Branches on the data the route's own loader produced rather than on the
	// flag: this component is shared, and only the loader knows which path ran.
	let useRest = $derived('restArtistSlug' in page.data);
	let slug = $derived((page.data.restArtistSlug ?? '') as string);

	// The REST page is stored in the same envelope the GraphQL page uses, so
	// the transform, getPageRows and getInfiniteData below are unchanged.
	let query = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: artistSectionQueryKey('exhibitions', slug, {
						limit: ARTIST_PROJECTS_LIMIT,
						offset: 0,
					}),
					queryFn: async (offset: number) => ({
						Artists: [
							{
								exhibitions: (
									await getArtistExhibitions(undefined, slug, {
										limit: ARTIST_PROJECTS_LIMIT,
										offset,
									})
								).items,
							},
						],
					}),
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
			: infiniteQuery({
					gqlClient,
					variables: { ...variables, sort: EXHIBITIONS_SORT },
					document: GetArtistPageExhibitionsDocument,
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
	);

	let exhibitions = $derived(
		getInfiniteData({
			query,
			transform: transformInfiniteDataExhibitions,
		})
	);

	// Use server-side data immediately, fall back to infinite query data once loaded
	let displayItems = $derived(
		exhibitions && exhibitions.length > 0
			? exhibitions
			: transformInfiniteDataExhibitions({
					Artists: [{ exhibitions: artist.exhibitions }],
				})
	);

	let hasMore = $derived(
		hasMoreInfiniteItems({
			query,
			totalCount: count,
			loadedCount: displayItems?.length || 0,
			limit: ARTIST_PROJECTS_LIMIT,
			getPageRows: (queryPage) => queryPage.Artists[0]?.exhibitions,
		})
	);

	const handleLoadMoreExhibitions = () => {
		setArtistSectionCount(
			'exhibitions',
			(displayItems?.length || 0) + ARTIST_PROJECTS_LIMIT
		);
		query.fetchNextPage();
	};

	// After back/forward navigation the TanStack cache usually still holds the
	// previously loaded pages; when it has been garbage-collected, catch back
	// up to the count persisted in the history entry page by page.
	$effect(() => {
		if (
			shouldRestoreInfiniteItems({
				query,
				targetCount: getArtistSectionCount('exhibitions'),
				loadedCount: displayItems?.length || 0,
			})
		) {
			query.fetchNextPage();
		}
	});
</script>

{#if displayItems && displayItems.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="EXHIBITIONS"
			href={GlobalRoutes.ExhibitionsHome}
			dataCy={dataCyPrefix}
			label="View all exhibitions"
		/>
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each displayItems as exhibition}
				<ExhibitionCard
					dataCy={dataCyPrefix}
					exhibition={{
						src: exhibition.src,
						title: exhibition.title,
						subtitle: exhibition.subtitle,
						dates: exhibition.dates,
						short_location_description: exhibition.short_location_description,
						secondaryButtonProps: exhibition.secondaryButtonProps,
					}}
				/>
			{/each}
		</div>
		{#if hasMore}
			<Button
				dataCy={`${dataCyPrefix}-load-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				loading={query.isFetchingNextPage}
				onclick={handleLoadMoreExhibitions}>Show more</Button
			>
		{/if}
	</div>
{/if}
